import './Event.css';
import React, { useState, useEffect } from 'react';
import { camelCase } from 'lodash';
import { useRemoteData } from '../../../../model/data';
import { useRouterParam } from '../../../../model/history';
import { useMeta, getPageTitle } from '../../../../model/meta';
import { eventsPath, eventPath, orderPath } from '../../../../routes';
import { PageHeader } from '../../../../components/PageHeader/PageHeader';
import { Container } from '../../../../components/Container/Container';
import { getEventDetails, getEventName, Event, getEventSalesStatus } from '../../../../model/event';
import { Tabs } from '../../../../components/Tabs/Tabs';
import { LoadingPage } from '../../../../components/LoadingPage/LoadingPage';
import { Details } from './Details/Details';
import { Contingent } from './Contingent/Contingent';
import { Ticket } from './Ticket/Ticket';
import { Route, Switch } from 'react-router-dom';
import { Overview } from './Overview/Overview';
import { Shop } from './Shop/Shop';
import { Activities, ActivitiesContext } from '../../../../components/Activities/Activities';
import { Icon } from '../../../../components/Icon/Icon';
import { Tooltip } from '../../../../components/Tooltip/Tooltip';
import { Ownerships } from '../../../Ownerships/Ownerships';
import { t } from '../../../../locales/translate';
import { ControlledModal } from '../../../../components/Modal/ControlledModal';
import { CompanyAssign, AssignCompany } from '../../../Ownerships/AssignCompany';
import { useConfirmation } from '../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { deleteOwnership, Ownership } from '../../../../model/ownerships';
import { useReloadKey } from '../../../../util/util';
import { writeDB } from '../../../../model/store';
import { Spacer } from '../../../../components/Spacer/Spacer';

function renderLabel(item: string): string {
  switch (item) {
    case 'overview':
      return t.general.overview;
    case 'details':
      return t.general.details;
    case 'contingent':
      return t.model.venueLayout.contingent;
    case 'prices':
      return t.model.price._plural;
    case 'ticket':
      return t.model.ticket._plural;
    case 'billingItem':
      return t.model.event.billingItem;
    default:
      return item;
  }
}

export function Event(): JSX.Element {
  const { id, section } = useRouterParam<{ id: string; section: string }>();
  const [event, loading, error] = useRemoteData(
    async () => {
      await getEventDetails({ eventId: id });
      const event = writeDB.table('events').where({ slug: id })[0] || writeDB.table('events').where({ id })[0];
      await getEventSalesStatus(event.id);
    },
    (db) => {
      return db.table('events').where({ slug: id })[0] || db.table('events').where({ id })[0];
    },
    [id]
  );

  const [addModal, setAddModal] = useState(false);
  const [selectedOwnership, setSelectedOwnership] = useState<Ownership | null>(null);

  async function handleDelete(): Promise<void> {
    if (!selectedOwnership) {
      return;
    }
    await deleteOwnership({ ownershipId: selectedOwnership.id });
    await getEventDetails({ eventId: event.id });
    setSelectedOwnership(null);
  }

  const { confirm } = useConfirmation({
    title: t.model.company.removeCompany,
    message: t.model.company.confirmRemoveCompany,
    action: handleDelete,
    onClose: () => setSelectedOwnership(null),
  });

  useEffect(() => {
    if (selectedOwnership) {
      confirm();
    }
  }, [selectedOwnership]);

  useMeta({
    title: getPageTitle(loading, getEventName(event), event),
    breadcrumbs: [
      { title: t.model.event._plural, url: eventsPath() },
      {
        title: getPageTitle(loading, getEventName(event), event),
      },
    ],
  });

  const [activitiesKey, reloadActivities] = useReloadKey();
  const [eventImportantActivitiesKey, reloadEventImportantActivities] = useReloadKey();

  if (loading && !event) {
    return <LoadingPage title={t.actions.loading(t.model.event._name)} />;
  } else if (error || !event) {
    return <Container>{t.errors.itemNotFound(t.model.event._name)}.</Container>;
  }

  const primarySections = ['overview', 'details', 'contingent', 'ticket'];
  const secondarySections: string[] = [];

  if (event.$permissions.includes('update_shop_settings')) {
    secondarySections.push('shop');
    if (event.clearingOrderId) {
      secondarySections.push('billingItem');
    }
  }

  function renderHeaderDescription(): string {
    return [
      event.happeningFrom && event.happeningFrom.getFullYear(),
      event.venue?.name,
      [event.venue?.address.city, event.venue?.address.countryCode].filter(Boolean).join(', '),
      event.sku,
    ]
      .filter(Boolean)
      .join(' - ');
  }

  function getPath(item: string, event: Event): string {
    if (item === 'overview') {
      return eventPath(event.slug);
    } else if (item === 'billingItem' && event.clearingOrderId) {
      return orderPath(event.clearingOrderId);
    } else {
      return `${eventPath(event.slug)}/${item}`;
    }
  }

  return (
    <ActivitiesContext.Provider value={{ reloadActivities }}>
      <div className="event">
        <PageHeader title={getEventName(event)} date={event.happeningFrom} description={renderHeaderDescription()} />
        <div className="event-tabs">
          <Tabs
            tabGroups={[
              primarySections.map((item) => ({
                title: renderLabel(item),
                active: item === 'overview' ? !section : section === camelCase(item),
                path: getPath(item, event),
              })),
              secondarySections.map((item) => ({
                title: renderLabel(item),
                active: section === camelCase(item),
                path: getPath(item, event),
              })),
            ]}
            accessory={
              <div className="event-tabs__add-company">
                <Tooltip
                  position="bottom"
                  disableRepositioning={false}
                  reference={<Icon name="users"></Icon>}
                  arrow
                  elevated
                  gap="medium"
                  mode="click"
                >
                  <Ownerships
                    product={event}
                    onAddCompany={() => setAddModal(true)}
                    onDeleteCompany={(item) => {
                      setSelectedOwnership(item);
                    }}
                  />
                </Tooltip>
              </div>
            }
          />
        </div>
        <Spacer size="lg" />
        <Container className="event">
          <Switch>
            <Route
              exact
              path={eventPath(':id')}
              render={() => <Overview event={event} eventImportantActivitiesKey={eventImportantActivitiesKey} />}
            />
            <Route exact path={eventPath(':id/details')} render={() => <Details event={event} />} />
            <Route exact path={eventPath(':id/contingent')} render={() => <Contingent event={event} />} />
            <Route exact path={eventPath(':id/ticket')} render={() => <Ticket event={event} />} />
            <Route exact path={eventPath(':id/ticket/:subsection')} render={() => <Ticket event={event} />} />
            <Route exact path={eventPath(':id/shop')} render={() => <Shop event={event} />} />
          </Switch>
          <Spacer size="lg" />
          <Activities
            subjectType="event"
            subjectId={event.id}
            cacheKey={activitiesKey}
            onFlag={reloadEventImportantActivities}
          />
        </Container>
        <ControlledModal<CompanyAssign>
          open={addModal}
          onClose={() => setAddModal(false)}
          title={t.actions.assign(t.model.company._name)}
          size="small"
        >
          {(controller) => (
            <AssignCompany
              productId={event.id}
              type="Event"
              controller={controller}
              onOwnershipCreated={async () => await getEventDetails({ eventId: event.id })}
            />
          )}
        </ControlledModal>
      </div>
    </ActivitiesContext.Provider>
  );
}
